import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X } from 'lucide-react';
import { usePermissions } from '@/hooks/usePermissions';
import { useCompany } from '@/contexts/CompanyContext';
import { getNavigationSections } from './navigation';

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const navigate = useNavigate();
  // This mode-aware hook uses .NET permissions under IDP and Firebase otherwise.
  const { hasPermission } = usePermissions();
  const { selectedCompany, isPartnerUser, isPartnerCompany } = useCompany();
  const searchRef = useRef<HTMLInputElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const calculationEnabled = selectedCompany?.settings?.calculation?.enabled ?? false;
  const destinations = useMemo(
    () => getNavigationSections({ isPartnerUser, isPartnerCompany, calculationEnabled, hasPermission })
      .flatMap((section) => section.items)
      .filter((item) => !item.externalHref),
    [isPartnerUser, isPartnerCompany, calculationEnabled, hasPermission],
  );
  const results = useMemo(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase('da');
    if (!normalizedQuery) return destinations;
    return destinations.filter((item) => item.label.toLocaleLowerCase('da').includes(normalizedQuery));
  }, [destinations, query]);

  useEffect(() => {
    if (!open) return;
    previousFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setQuery('');
    setActiveIndex(0);
    searchRef.current?.focus();

    return () => {
      previousFocusRef.current?.focus();
      previousFocusRef.current = null;
    };
  }, [open]);

  useEffect(() => {
    setActiveIndex((index) => Math.min(index, Math.max(results.length - 1, 0)));
  }, [results.length]);

  if (!open) return null;

  const openDestination = (path: string) => {
    onOpenChange(false);
    navigate(path);
  };

  const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown' && results.length > 0) {
      event.preventDefault();
      setActiveIndex((index) => (index + 1) % results.length);
    } else if (event.key === 'ArrowUp' && results.length > 0) {
      event.preventDefault();
      setActiveIndex((index) => (index - 1 + results.length) % results.length);
    } else if (event.key === 'Enter' && results[activeIndex]) {
      event.preventDefault();
      openDestination(results[activeIndex].path);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      onOpenChange(false);
    } else if (event.key === 'Tab') {
      event.preventDefault();
      closeButtonRef.current?.focus();
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-start justify-center bg-black/45 px-4 pt-[12vh] backdrop-blur-[2px]"
      onClick={(event) => {
        if (event.target === event.currentTarget) onOpenChange(false);
      }}
    >
      <section
        aria-labelledby="command-palette-title"
        aria-modal="true"
        className="w-full max-w-xl overflow-hidden rounded-xl bg-white shadow-2xl dark:bg-dark-card"
        role="dialog"
      >
        <h2 className="sr-only" id="command-palette-title">Søg efter genvej</h2>
        <div className="flex items-center gap-3 border-b border-bordergray px-4 dark:border-dark-border">
          <Search aria-hidden="true" className="h-5 w-5 shrink-0 text-muted dark:text-dark-muted" />
          <input
            ref={searchRef}
            aria-activedescendant={results.length > 0 ? `command-palette-option-${activeIndex}` : undefined}
            aria-autocomplete="list"
            aria-controls="command-palette-results"
            aria-label="Søg efter en genvej"
            autoComplete="off"
            className="h-14 min-w-0 flex-1 rounded-sm border-b-2 border-b-transparent bg-transparent text-[15px] text-dark outline-none placeholder:text-muted focus-visible:border-b-gray-500 dark:text-dark-text dark:placeholder:text-dark-muted dark:focus-visible:border-b-gray-400"
            onChange={(event) => {
              setQuery(event.target.value);
              setActiveIndex(0);
            }}
            onKeyDown={handleSearchKeyDown}
            placeholder="Find side eller funktion..."
            role="combobox"
            value={query}
          />
          <button
            aria-label="Luk genveje"
            className="rounded-md p-1.5 text-muted hover:bg-surface hover:text-dark focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary dark:text-dark-muted dark:hover:bg-dark-hover dark:hover:text-dark-text"
            onClick={() => onOpenChange(false)}
            onKeyDown={(event) => {
              if (event.key !== 'Tab') return;
              event.preventDefault();
              searchRef.current?.focus();
            }}
            ref={closeButtonRef}
            type="button"
          >
            <X aria-hidden="true" className="h-4 w-4" />
          </button>
        </div>
        <div className="max-h-[min(60vh,28rem)] overflow-y-auto p-2">
          {results.length === 0 ? (
            <p className="px-3 py-8 text-center text-sm text-muted dark:text-dark-muted">Ingen genvej matcher søgningen.</p>
          ) : (
            <ul aria-label="Tilgængelige genveje" className="space-y-1" id="command-palette-results" role="listbox">
              {results.map((item, index) => (
                <li
                  aria-selected={index === activeIndex}
                  className={`flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 text-sm ${
                    index === activeIndex
                      ? 'bg-primary text-white'
                      : 'text-dark hover:bg-surface dark:text-dark-text dark:hover:bg-dark-hover'
                  }`}
                  id={`command-palette-option-${index}`}
                  key={item.path}
                  onClick={() => openDestination(item.path)}
                  onMouseEnter={() => setActiveIndex(index)}
                  role="option"
                >
                  <item.icon aria-hidden="true" className="h-4 w-4 shrink-0" />
                  <span>{item.label}</span>
                  {index === activeIndex && <span className="ml-auto text-xs opacity-75">Enter</span>}
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="flex items-center gap-4 border-t border-bordergray px-4 py-2.5 text-xs text-muted dark:border-dark-border dark:text-dark-muted">
          <span><kbd className="rounded border border-bordergray px-1.5 py-0.5 dark:border-dark-border">↑</kbd> <kbd className="rounded border border-bordergray px-1.5 py-0.5 dark:border-dark-border">↓</kbd> naviger</span>
          <span><kbd className="rounded border border-bordergray px-1.5 py-0.5 dark:border-dark-border">Enter</kbd> vælg</span>
          <span><kbd className="rounded border border-bordergray px-1.5 py-0.5 dark:border-dark-border">Esc</kbd> luk</span>
        </div>
      </section>
    </div>
  );
}
